import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
  FiDatabase,
  FiDownload,
  FiTrash2,
  FiRefreshCw,
  FiSettings,
  FiShield,
  FiClock,
  FiHardDrive,
  FiCheckCircle,
  FiXCircle,
  FiPlus,
  FiRotateCcw,
  FiActivity
} from 'react-icons/fi';

const BackupManagement = () => {
  const { user } = useAuth();
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [backupOptions, setBackupOptions] = useState({
    includeUploads: true,
    compression: true,
    encryption: true,
    description: ''
  });
  const [restoreOptions, setRestoreOptions] = useState({
    includeUploads: true,
    includeConfig: true
  });

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchBackups();
      fetchStats();
      fetchConfig();
    }
  }, [user]);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/backup/list');
      setBackups(response.data.data);
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast('Erreur lors de la récupération des sauvegardes', { icon: '❌' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/backup/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching backup stats:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await api.get('/api/backup/config');
      setConfig(response.data.data);
    } catch (error) {
      console.error('Error fetching backup config:', error);
    }
  };

  const createBackup = async () => {
    try {
      setCreatingBackup(true);
      
      // Show progress message
      toast('Création de la sauvegarde en cours... Cela peut prendre quelques minutes.', {
        icon: '⏳',
        duration: 4000
      });
      
      console.log('🚀 Sending backup request with options:', backupOptions);
      
      const response = await api.post('/api/backup/create', backupOptions);
      
      console.log('📡 Response received:', response);
      console.log('📊 Response data:', response.data);
      console.log('✅ Response success:', response.data.success);
      
      if (response.data && response.data.success === true) {
        toast('Sauvegarde créée avec succès', { icon: '✅' });
        setShowCreateModal(false);
        setBackupOptions({
          includeUploads: true,
          compression: true,
          encryption: true,
          description: ''
        });
        fetchBackups();
        fetchStats();
      } else {
        console.error('❌ Response indicates failure:', response.data);
        toast('Erreur lors de la création de la sauvegarde', { icon: '❌' });
      }
    } catch (error) {
      console.error('❌ Error creating backup:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        code: error.code,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Handle different types of errors
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast('La création de la sauvegarde a pris trop de temps. Vérifiez la page pour voir si elle a été créée.', { icon: '⏰' });
        // Refresh the backup list to check if backup was actually created
        fetchBackups();
        fetchStats();
      } else if (error.response?.status === 500) {
        toast('Erreur serveur lors de la création de la sauvegarde', { icon: '❌' });
      } else if (error.response?.status === 401) {
        toast('Session expirée. Veuillez vous reconnecter.', { icon: '🔒' });
      } else {
        toast('Erreur lors de la création de la sauvegarde', { icon: '❌' });
      }
    } finally {
      setCreatingBackup(false);
    }
  };

  const restoreBackup = async (backupName) => {
    try {
      setRestoringBackup(backupName);
      const response = await api.post(`/api/backup/restore/${backupName}`, restoreOptions);
      toast('Sauvegarde restaurée avec succès', { icon: '✅' });
      setShowRestoreModal(false);
      setRestoringBackup(null);
      setSelectedBackup(null);
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast('Erreur lors de la restauration de la sauvegarde', { icon: '❌' });
    } finally {
      setRestoringBackup(null);
    }
  };

  const validateBackup = async (backupName) => {
    try {
      const response = await api.post(`/api/backup/validate/${backupName}`);
      toast('Sauvegarde validée avec succès', { icon: '✅' });
    } catch (error) {
      console.error('Error validating backup:', error);
      toast('Erreur lors de la validation de la sauvegarde', { icon: '❌' });
    }
  };

  const deleteBackup = async (backupName) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette sauvegarde ?')) {
      return;
    }

    try {
      await api.delete(`/api/backup/delete/${backupName}`);
      toast('Sauvegarde supprimée avec succès', { icon: '✅' });
      fetchBackups();
      fetchStats();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast('Erreur lors de la suppression de la sauvegarde', { icon: '❌' });
    }
  };

  const downloadBackup = async (backupName) => {
    try {
      const response = await api.get(`/api/backup/download/${backupName}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backupName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast('Téléchargement démarré', { icon: '⬇️' });
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast('Erreur lors du téléchargement de la sauvegarde', { icon: '❌' });
    }
  };

  const updateConfig = async () => {
    try {
      const response = await api.put('/api/backup/config', config);
      toast('Configuration mise à jour avec succès', { icon: '✅' });
      setShowConfigModal(false);
      fetchConfig();
    } catch (error) {
      console.error('Error updating config:', error);
      toast('Erreur lors de la mise à jour de la configuration', { icon: '❌' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const getStatusIcon = (backup) => {
    if (backup.isEncrypted) {
      return <FiShield className="w-4 h-4 text-green-600" title="Chiffré" />;
    }
    if (backup.isCompressed) {
      return <FiHardDrive className="w-4 h-4 text-blue-600" title="Compressé" />;
    }
    return <FiDatabase className="w-4 h-4 text-gray-600" title="Standard" />;
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FiXCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Accès Refusé
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Seuls les super administrateurs peuvent accéder à la gestion des sauvegardes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestion des Sauvegardes
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Sauvegardez et restaurez votre base de données automatiquement
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setShowConfigModal(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <FiSettings className="w-4 h-4" />
            Configuration
          </button>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <FiPlus className="w-4 h-4" />
            Nouvelle Sauvegarde
          </button>
          
          <button
            onClick={fetchBackups}
            disabled={loading}
            className="btn btn-secondary flex items-center gap-2"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Sauvegardes</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalBackups}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                <FiDatabase className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Taille Totale</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalSize}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <FiHardDrive className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Dernière Sauvegarde</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {stats.lastBackup ? formatDate(stats.lastBackup) : 'Aucune'}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                <FiClock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Prochaine Sauvegarde</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {stats.nextScheduledBackup ? formatDate(stats.nextScheduledBackup) : 'N/A'}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center">
                <FiActivity className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backups List */}
      <div className="card">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Sauvegardes Disponibles
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Chargement des sauvegardes...</p>
            </div>
          ) : backups.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FiDatabase className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p>Aucune sauvegarde trouvée</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taille
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Créé le
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {backups.map((backup) => (
                  <tr key={backup.name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(backup)}
                        <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                          {backup.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatFileSize(backup.size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatDate(backup.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {backup.isEncrypted && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Chiffré
                          </span>
                        )}
                        {backup.isCompressed && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Compressé
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedBackup(backup);
                            setShowRestoreModal(true);
                          }}
                          className="btn btn-xs btn-secondary"
                          title="Restaurer"
                        >
                          <FiRotateCcw className="w-3 h-3" />
                        </button>
                        
                        <button
                          onClick={() => validateBackup(backup.name)}
                          className="btn btn-xs btn-secondary"
                          title="Valider"
                        >
                          <FiCheckCircle className="w-3 h-3" />
                        </button>
                        
                        <button
                          onClick={() => downloadBackup(backup.name)}
                          className="btn btn-xs btn-primary"
                          title="Télécharger"
                        >
                          <FiDownload className="w-3 h-3" />
                        </button>
                        
                        <button
                          onClick={() => deleteBackup(backup.name)}
                          className="btn btn-xs btn-danger"
                          title="Supprimer"
                        >
                          <FiTrash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Backup Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Créer une Nouvelle Sauvegarde
            </h3>
            
            <div className="space-y-4">
              {creatingBackup && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <div>
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        Création de la sauvegarde en cours...
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-300">
                        Cette opération peut prendre plusieurs minutes. Veuillez patienter.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={backupOptions.description}
                  onChange={(e) => setBackupOptions({...backupOptions, description: e.target.value})}
                  placeholder="Description de la sauvegarde"
                  className="input w-full"
                  disabled={creatingBackup}
                />
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={backupOptions.includeUploads}
                    onChange={(e) => setBackupOptions({...backupOptions, includeUploads: e.target.checked})}
                    className="mr-2"
                    disabled={creatingBackup}
                  />
                  Inclure les fichiers uploadés
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={backupOptions.compression}
                    onChange={(e) => setBackupOptions({...backupOptions, compression: e.target.checked})}
                    className="mr-2"
                    disabled={creatingBackup}
                  />
                  Compresser la sauvegarde
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={backupOptions.encryption}
                    onChange={(e) => setBackupOptions({...backupOptions, encryption: e.target.checked})}
                    className="mr-2"
                    disabled={creatingBackup}
                  />
                  Chiffrer la sauvegarde
                </label>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn btn-secondary"
                disabled={creatingBackup}
              >
                Annuler
              </button>
              <button
                onClick={createBackup}
                className="btn btn-primary"
                disabled={creatingBackup}
              >
                {creatingBackup ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Création en cours...
                  </div>
                ) : (
                  'Créer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Backup Modal */}
      {showRestoreModal && selectedBackup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Restaurer la Sauvegarde
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Êtes-vous sûr de vouloir restaurer la sauvegarde <strong>{selectedBackup.name}</strong> ?
              </p>
              <p className="text-sm text-red-600 mt-2">
                ⚠️ Cette action remplacera toutes les données actuelles !
              </p>
            </div>
            
            <div className="space-y-2 mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={restoreOptions.includeUploads}
                  onChange={(e) => setRestoreOptions({...restoreOptions, includeUploads: e.target.checked})}
                  className="mr-2"
                />
                Restaurer les fichiers uploadés
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={restoreOptions.includeConfig}
                  onChange={(e) => setRestoreOptions({...restoreOptions, includeConfig: e.target.checked})}
                  className="mr-2"
                />
                Restaurer la configuration
              </label>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRestoreModal(false)}
                className="btn btn-secondary"
                disabled={restoringBackup}
              >
                Annuler
              </button>
              <button
                onClick={() => restoreBackup(selectedBackup.name)}
                className="btn btn-danger"
                disabled={restoringBackup}
              >
                {restoringBackup ? 'Restauration...' : 'Restaurer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && config && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Configuration des Sauvegardes
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Répertoire de sauvegarde
                </label>
                <input
                  type="text"
                  value={config.backupDirectory}
                  disabled
                  className="input w-full bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre maximum de sauvegardes
                </label>
                <input
                  type="number"
                  value={config.maxBackups}
                  onChange={(e) => setConfig({...config, maxBackups: parseInt(e.target.value)})}
                  min="1"
                  max="100"
                  className="input w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Clé de chiffrement
                </label>
                <input
                  type="password"
                  value={config.encryptionEnabled ? '***ENCRYPTED***' : ''}
                  onChange={(e) => setConfig({...config, encryptionKey: e.target.value})}
                  placeholder="Nouvelle clé de chiffrement"
                  className="input w-full"
                />
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Sauvegardes programmées</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <div>• Quotidienne: {config.scheduledBackups.daily}</div>
                  <div>• Hebdomadaire: {config.scheduledBackups.weekly}</div>
                  <div>• Mensuelle: {config.scheduledBackups.monthly}</div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowConfigModal(false)}
                className="btn btn-secondary"
              >
                Fermer
              </button>
              <button
                onClick={updateConfig}
                className="btn btn-primary"
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupManagement;
